import React, { useState, useMemo } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { useProductos } from '../../hooks/useProductos';
import { ROUTES, CATEGORIAS } from '../../constants';
import { useFiltros } from '../../hooks/useFiltros';
import { compareProductosPorModelo } from '../../utils/modeloOrder';
import { usePreciosMasivo } from '../../hooks/usePreciosMasivo';
import { obtenerValoresUnicos, verificarPreciosUniforme, verificarCostosUniforme } from '../../utils/productUtils';
import { formatPrecio } from '../../utils/formatters';
import FiltrosProductos from '../../components/admin/FiltrosProductos';
import TablaProductos from '../../components/admin/TablaProductos';
import ModalActualizarPrecios from '../../components/admin/ModalActualizarPrecios';
import ModalActualizarCostos from '../../components/admin/ModalActualizarCostos';
import './Admin.css';

function AdminProductos() {
  const navigate = useNavigate();
  const { productos, loading, error, refetch } = useProductos();
  const {
    filtros,
    productosFiltrados,
    handleFiltroChange,
    limpiarFiltros,
    hayFiltrosActivos
  } = useFiltros(productos, ['modelo', 'categoria', 'material', 'color']);

  const [showModalPrecios, setShowModalPrecios] = useState(false);
  const [showModalCostos, setShowModalCostos] = useState(false);
  const { updating, error: precioError, actualizarPrecios, actualizarCostos } = usePreciosMasivo();

  // Obtener valores únicos para los selects de filtro
  const valoresUnicos = useMemo(() => {
    return {
      colores: obtenerValoresUnicos(productos, 'color'),
      materiales: obtenerValoresUnicos(productos, 'material'),
      categorias: obtenerValoresUnicos(productos, 'categoria')
    };
  }, [productos]);

  // Ordenar fundas por material antes de mostrar
  const productosOrdenadosPorMaterial = useMemo(() => {
    // Orden fijo para materiales de fundas: magsafe, transparente, silicona, luego el resto alfabético
    const materialOrder = { magsafe: 0, transparente: 1, silicona: 2 };
    
    return [...productosFiltrados].sort((a, b) => {
      // Solo ordenar fundas por material
      if (a.categoria?.toLowerCase() === CATEGORIAS.FUNDAS && b.categoria?.toLowerCase() === CATEGORIAS.FUNDAS) {
        const aKey = (a.material || '').toLowerCase();
        const bKey = (b.material || '').toLowerCase();
        const aRank = materialOrder.hasOwnProperty(aKey) ? materialOrder[aKey] : Number.POSITIVE_INFINITY;
        const bRank = materialOrder.hasOwnProperty(bKey) ? materialOrder[bKey] : Number.POSITIVE_INFINITY;
        if (aRank !== bRank) return aRank - bRank;
        return compareProductosPorModelo(a, b);
      }
      return compareProductosPorModelo(a, b);
    });
  }, [productosFiltrados]);

  // Verificar si los precios son uniformes
  const preciosUniforme = useMemo(() => {
    return verificarPreciosUniforme(productosFiltrados);
  }, [productosFiltrados]);

  const costosUniforme = useMemo(() => {
    return verificarCostosUniforme(productosFiltrados);
  }, [productosFiltrados]);

  const handleAbrirModalPrecios = () => {
    setShowModalPrecios(true);
  };

  const handleCerrarModalPrecios = () => {
    setShowModalPrecios(false);
  };

  const handleAbrirModalCostos = () => {
    setShowModalCostos(true);
  };

  const handleCerrarModalCostos = () => {
    setShowModalCostos(false);
  };

  const handleActualizarPrecios = async (preciosForm) => {
    const ids = productosFiltrados.map(p => p.id);
    const result = await actualizarPrecios(ids, preciosForm);
    
    if (result.success) {
      await refetch();
      handleCerrarModalPrecios();
    }
  };

  const handleActualizarCostos = async (costo) => {
    const ids = productosFiltrados.map(p => p.id);
    const result = await actualizarCostos(ids, costo);

    if (result.success) {
      await refetch();
      handleCerrarModalCostos();
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="admin-section-title mb-0">Productos</h5>
        <Button 
          variant="primary" 
          size="sm"
          onClick={() => navigate(`${ROUTES.ADMIN_PRODUCTOS}/nuevo`)}
          className="d-inline-flex align-items-center gap-2"
        >
          <FaPlus />
          Agregar Producto
        </Button>
      </div>

      {/* Filtros */}
      {!loading && !error && productos.length > 0 && (
        <FiltrosProductos
          filtros={filtros}
          onFiltroChange={handleFiltroChange}
          onLimpiar={limpiarFiltros}
          valoresUnicos={valoresUnicos}
        />
      )}

      {/* Botón para actualizar precios cuando hay filtros activos */}
      {!loading && !error && hayFiltrosActivos && productosFiltrados.length > 0 && (
        <div className="admin-form-container mb-3 p-3 d-flex justify-content-between align-items-center">
          <div>
            <strong>{productosFiltrados.length}</strong> producto{productosFiltrados.length !== 1 ? 's' : ''} encontrado{productosFiltrados.length !== 1 ? 's' : ''}
            {preciosUniforme && (
              <span className="text-muted ms-2">
                (Precios uniformes: Mayorista: {formatPrecio(preciosUniforme.precio)}, Minorista: {formatPrecio(preciosUniforme.preciomenor)})
              </span>
            )}
            {costosUniforme != null && costosUniforme !== '' && (
              <span className="text-muted ms-2">
                (Costo uniforme: {formatPrecio(costosUniforme)})
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="success"
              size="sm"
              onClick={handleAbrirModalPrecios}
              style={{ padding: '0.5rem 1.25rem', fontWeight: 500 }}
            >
              Actualizar Precios ({productosFiltrados.length})
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleAbrirModalCostos}
              style={{ padding: '0.5rem 1.25rem', fontWeight: 500 }}
            >
              Actualizar Costos ({productosFiltrados.length})
            </Button>
          </div>
        </div>
      )}

      {loading && <Spinner animation="border" size="sm" />}
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {precioError && <Alert variant="danger" className="mt-3">{precioError}</Alert>}

      {!loading && !error && productosFiltrados.length > 0 && (
        <TablaProductos productos={productosOrdenadosPorMaterial} onEliminar={refetch} />
      )}

      {!loading && !error && productos.length === 0 && (
        <Alert variant="info" className="mt-3">No hay productos para mostrar</Alert>
      )}

      {!loading && !error && productos.length > 0 && productosFiltrados.length === 0 && (
        <Alert variant="warning" className="mt-3">
          No se encontraron productos que coincidan con los filtros aplicados.
        </Alert>
      )}

      {/* Modal para actualizar precios */}
      <ModalActualizarPrecios
        show={showModalPrecios}
        onHide={handleCerrarModalPrecios}
        productos={productosOrdenadosPorMaterial}
        preciosUniforme={preciosUniforme}
        onActualizar={handleActualizarPrecios}
        updating={updating}
        error={precioError}
      />
      <ModalActualizarCostos
        show={showModalCostos}
        onHide={handleCerrarModalCostos}
        productos={productosOrdenadosPorMaterial}
        costoUniforme={costosUniforme}
        onActualizar={handleActualizarCostos}
        updating={updating}
        error={precioError}
      />
    </Container>
  );
}

export default AdminProductos;
